/**
 * BootScene — loads art, then hands off to GameScene.
 * Keep all asset loading here so other scenes can assume textures exist.
 */

export class BootScene extends Phaser.Scene {
  constructor() {
    super('Boot');
  }

  preload() {
    // Approved Knight sprites (Phase 1), sliced to transparent PNGs.
    this.load.image('knight_front', 'assets/sprites/knight_front.png');
    this.load.image('knight_side', 'assets/sprites/knight_side.png');
    this.load.image('knight_back', 'assets/sprites/knight_back.png');

    // Per-class HUD emblems (shown beside each hero's HP bar).
    for (const c of ['knight', 'rogue', 'ranger', 'pyromancer', 'priest']) {
      this.load.image(`class_${c}`, `assets/sprites/ui/class_${c}.png`);
    }

    // Approved monsters for Green Meadows (Phase 2), side-profile.
    this.load.image('mob_rat', 'assets/sprites/monsters/rat_side.png');
    this.load.image('mob_bird', 'assets/sprites/monsters/bird_side.png');
    this.load.image('mob_wolf', 'assets/sprites/monsters/wolf_side.png');
    this.load.image('mob_alpha_wolf', 'assets/sprites/monsters/alpha_wolf_side.png');

    // Attack-pose textures for the Green Meadows mobs — swapped in during the
    // lunge so the static sprites have a real strike animation (idle ↔ attack).
    for (const m of ['rat', 'bird', 'wolf', 'alpha_wolf']) {
      this.load.image(`mob_${m}_atk`, `assets/sprites/monsters/${m}_atk.png`);
    }

    // Dark Forest monsters (Phase 4).
    this.load.image('mob_spider', 'assets/sprites/monsters/spider_side.png');
    this.load.image('mob_bandit', 'assets/sprites/monsters/bandit_side.png');
    this.load.image('mob_treant', 'assets/sprites/monsters/treant_side.png');
    this.load.image('mob_elder_treant', 'assets/sprites/monsters/elder_treant_side.png');

    // Celestial Throne (Tier 7) — static single-pose sprites (animated by code).
    this.load.image('mob_storm_harpy', 'assets/sprites/monsters/storm_harpy_side.png');
    this.load.image('mob_astral_golem', 'assets/sprites/monsters/astral_golem_side.png');
    this.load.image('mob_seraph_sentinel', 'assets/sprites/monsters/seraph_sentinel_side.png');
    this.load.image('mob_celestial_archon', 'assets/sprites/monsters/celestial_archon_side.png');

    // Vazio Estelar (Tier 8) — static single-pose sprites (animated by code).
    this.load.image('mob_nebula_wraith', 'assets/sprites/monsters/nebula_wraith_side.png');
    this.load.image('mob_astral_serpent', 'assets/sprites/monsters/astral_serpent_side.png');
    this.load.image('mob_cosmic_devourer', 'assets/sprites/monsters/cosmic_devourer_side.png');
    this.load.image('mob_star_titan', 'assets/sprites/monsters/star_titan_side.png');

    // Necrópole Eterna (Tier 9) — static single-pose sprites (animated by code).
    this.load.image('mob_bone_knight', 'assets/sprites/monsters/bone_knight_side.png');
    this.load.image('mob_plague_lich', 'assets/sprites/monsters/plague_lich_side.png');
    this.load.image('mob_wraith_king', 'assets/sprites/monsters/wraith_king_side.png');
    this.load.image('mob_death_sovereign', 'assets/sprites/monsters/death_sovereign_side.png');

    // Inferno Carmesim (Tier 10) — static single-pose sprites (animated by code).
    this.load.image('mob_hellhound', 'assets/sprites/monsters/hellhound_side.png');
    this.load.image('mob_flame_demon', 'assets/sprites/monsters/flame_demon_side.png');
    this.load.image('mob_dread_knight', 'assets/sprites/monsters/dread_knight_side.png');
    this.load.image('mob_demon_lord', 'assets/sprites/monsters/demon_lord_side.png');

    // Caverna Cristalina (Tier 11) — static single-pose sprites (animated by code).
    this.load.image('mob_crystal_shardling', 'assets/sprites/monsters/crystal_shardling_side.png');
    this.load.image('mob_prism_stalker', 'assets/sprites/monsters/prism_stalker_side.png');
    this.load.image('mob_geode_brute', 'assets/sprites/monsters/geode_brute_side.png');
    this.load.image('mob_crystal_monarch', 'assets/sprites/monsters/crystal_monarch_side.png');

    // Deserto Maldito (Tier 12) — static single-pose sprites (animated by code).
    this.load.image('mob_sand_wraith', 'assets/sprites/monsters/sand_wraith_side.png');
    this.load.image('mob_bone_scorpion', 'assets/sprites/monsters/bone_scorpion_side.png');
    this.load.image('mob_tomb_guardian', 'assets/sprites/monsters/tomb_guardian_side.png');
    this.load.image('mob_cursed_pharaoh', 'assets/sprites/monsters/cursed_pharaoh_side.png');

    // Pântano Putrefato (Tier 13) — static single-pose sprites (animated by code).
    this.load.image('mob_bog_hag', 'assets/sprites/monsters/bog_hag_side.png');
    this.load.image('mob_plague_toad', 'assets/sprites/monsters/plague_toad_side.png');
    this.load.image('mob_rot_behemoth', 'assets/sprites/monsters/rot_behemoth_side.png');
    this.load.image('mob_swamp_horror', 'assets/sprites/monsters/swamp_horror_side.png');

    // Cidadela Mecânica (Tier 14) — static single-pose sprites (animated by code).
    this.load.image('mob_clockwork_soldier', 'assets/sprites/monsters/clockwork_soldier_side.png');
    this.load.image('mob_brass_spider', 'assets/sprites/monsters/brass_spider_side.png');
    this.load.image('mob_steam_golem', 'assets/sprites/monsters/steam_golem_side.png');
    this.load.image('mob_mechanical_overlord', 'assets/sprites/monsters/mechanical_overlord_side.png');

    // Scrolling scenarios — the *_tile.png variants are [image | mirror] so the
    // background tiles horizontally with no seam as it scrolls (make_bg_tileable.py).
    this.load.image('bg_meadow', 'assets/tiles/meadow_bg_tile.png');
    this.load.image('bg_dark_forest', 'assets/tiles/dark_forest_bg_tile.png');
    this.load.image('bg_frozen_peaks', 'assets/tiles/frozen_peaks_bg_tile.png');
    this.load.image('bg_lava_caves', 'assets/tiles/lava_caves_bg_tile.png');
    this.load.image('bg_shadow_abyss', 'assets/tiles/shadow_abyss_bg_tile.png');
    this.load.image('bg_drowned_depths', 'assets/tiles/drowned_depths_bg_tile.png');
    this.load.image('bg_celestial_throne', 'assets/tiles/celestial_throne_bg_tile.png');
    this.load.image('bg_vazio_estelar', 'assets/tiles/vazio_estelar_bg_tile.png');
    this.load.image('bg_eternal_necropolis', 'assets/tiles/eternal_necropolis_bg_tile.png');
    this.load.image('bg_crimson_inferno', 'assets/tiles/crimson_inferno_bg_tile.png');
    this.load.image('bg_crystal_caverns', 'assets/tiles/crystal_caverns_bg_tile.png');
    this.load.image('bg_cursed_desert', 'assets/tiles/cursed_desert_bg_tile.png');
    this.load.image('bg_putrid_swamp', 'assets/tiles/putrid_swamp_bg_tile.png');
    this.load.image('bg_mechanical_citadel', 'assets/tiles/mechanical_citadel_bg_tile.png');

    // Animation frames for every playable class (walk loop + attack one-shot).
    const CLASSES = ['knight', 'ranger', 'priest', 'pyromancer', 'rogue'];
    for (const cls of CLASSES) {
      for (let i = 0; i < 4; i++) {
        this.load.image(`${cls}_walk_${i}`, `assets/sprites/anim/${cls}_walk_${i}.png`);
        this.load.image(`${cls}_attack_${i}`, `assets/sprites/anim/${cls}_attack_${i}.png`);
      }
    }

    // Animation frames for all monsters (walk loop + attack one-shot).
    // NOTE: green_meadows monsters (rat..alpha_wolf) intentionally omitted — they
    // ship as single hand-drawn static sprites driven by the code-based idle bob
    // + lunge attack. The dark_forest quartet (spider/bandit/treant/elder_treant)
    // now has real side-profile walk+attack frames (slice_tier2.py).
    const MONSTERS = [
      'spider', 'bandit', 'treant', 'elder_treant',
      'frost_imp', 'ice_golem', 'frost_wraith', 'frost_giant',
      'fire_elemental', 'magma_golem', 'lava_serpent', 'inferno_dragon',
      'shadow_fiend', 'void_golem', 'soul_reaper', 'void_titan',
      'abyss_angler', 'drowned_knight', 'deep_lurker', 'leviathan'];
    for (const mob of MONSTERS) {
      for (let i = 0; i < 4; i++) {
        this.load.image(`mob_${mob}_walk_${i}`, `assets/sprites/anim/mob_${mob}_walk_${i}.png`);
        this.load.image(`mob_${mob}_attack_${i}`, `assets/sprites/anim/mob_${mob}_attack_${i}.png`);
      }
    }

    // Signature-magic attack effect sprites: tier-3 skill visuals plus the
    // tier-6 ultimate strikes (one per class, sliced by tools/slice_fx.py).
    for (const fx of [
      'fire_lightning', 'celestial_blade', 'dagger_rain', 'arrow_storm', 'divine_beam',
      'godslayer_sword', 'void_rift', 'supernova', 'seraph_pillar', 'meteor_volley',
    ]) {
      this.load.image(`magic_${fx}`, `assets/sprites/fx/magic_${fx}.png`);
    }

    // A 1x1 magenta fallback so a missing texture is obvious, never fatal.
    const g = this.make.graphics({ x: 0, y: 0 });
    g.fillStyle(0xff00ff, 1).fillRect(0, 0, 16, 16);
    g.generateTexture('__missing', 16, 16);
    g.destroy();
  }

  create() {
    this.scene.start('Game', {
      state: this.registry.get('state'),
      data: this.registry.get('data'),
    });
  }
}
